import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Text,
  VStack,
  Button,
  HStack,
  Heading,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { IframeOrFullPageWrapper } from "components/Container";
import Header from "flows/components/Header";
import { FullPageSpinner } from "components/Spinner";
import { useSessionJWT, FLOW_STATE } from "utils";
import { getGDPRDocuments } from "api/pii";
import { useFlowConfig } from "flows/hooks";

const DocumentDownloadButton = ({ documentPayload, revIndex }) => {
  const toast = useToast();
  const { formatMessage } = useIntl();
  const { sessionJWT } = useSessionJWT();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const { revisions, pii_id } = documentPayload;
  const docRevision = revisions[revIndex];
  const { file_name } = docRevision;

  useEffect(() => {
    if (loadingError) {
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Popup title",
        }),
        description:
          formatMessage({
            defaultMessage: "There was an error downloading the document.",
            description: "error description",
          }),
        status: "error",
        isClosable: true,
      });
    }
  }, [loadingError, toast, formatMessage]); // TODO: add toast message here

  const handleClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/ui/api/gdpr/document/download`, {
        method: "POST",
        body: JSON.stringify(docRevision),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          authorization: `Bearer ${sessionJWT.jwt}`,
        },
      });
      if (!response.ok) {
        throw new Error("api error..."); // error.message is not shown in this case, just a generic download error message
      }
      // Convert response to a Blob
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", file_name);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    } catch (err) {
      setLoadingError(err);
    }
    setIsLoading(false);
  }, [documentPayload, revIndex, setIsLoading, sessionJWT]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Button
      onClick={handleClick}
      isDisabled={isLoading}
      isLoading={isLoading}
      data-testid={`download-button-${pii_id}-${revIndex}`}
    >
      Download
    </Button>
  );
};

const GDPR = ({ onReady, onComplete }) => {
  const { sessionJWT } = useSessionJWT();
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [docs, setDocs] = useState(null);
  const [loadingErr, setLoadingErr] = useState(null);
  const { hideExitButtons } = useFlowConfig();

  useEffect(onReady, [onReady]); // Trigger the onReady effect when the component mounts

  const loadDocs = useCallback(async () => {
    setIsLoading(true);
    setDocs(null);
    setLoadingErr(null);
    try {
      const docs = await getGDPRDocuments(sessionJWT);
      setDocs(docs);
    } catch (err) {
      setLoadingErr(err);
    }
    setIsLoading(false);
  }, [sessionJWT]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const handleCancel = () => {
    onComplete({ state: FLOW_STATE.CANCEL });
  };

  let content;
  if (isLoading) {
    content = <FullPageSpinner />;
  } else if (loadingErr) {
    content = (
      <Alert status="error">
        <AlertIcon />
        <FormattedMessage
          defaultMessage="There was an error retrieving your GDPR documents.  Please try again later."
          description="Section header"
        />
      </Alert>
    );
  } else if (docs) {
    const docDetails = (
      <VStack py="4" spacing={4} align="stretch">
        {(docs.payload?.documents || []).length === 0 ? (
          <HStack p="4" borderWidth="1px" borderRadius="4px" justify="space-between">
            <Text color="secondaryText" fontSize="sm" as="i">
              <FormattedMessage
                defaultMessage="No GDPR documents found."
                description="User message"
              />
            </Text>
          </HStack>
        ) : (
          (docs.payload?.documents || []).map((d, docIndex) => {
            return (d.revisions || []).map((r, i) => {
              return (
                <HStack key={`doc-${docIndex}-rev-${i}`} p="4" borderWidth="1px" borderRadius="4px" justify="space-between">
                  <Text color="secondaryText" fontSize="md">
                    {r.file_name}
                  </Text>
                  <DocumentDownloadButton documentPayload={d} revIndex={i} />
                </HStack>
              );
            });
          })
        )}
      </VStack>
    );
    content = (
      <Box pt="8">
        <Heading>
          <FormattedMessage
            defaultMessage="Your GDPR Information Request is Ready."
            description="header"
          />
        </Heading>
        <Text fontSize="sm" py="4">
          <FormattedMessage
            defaultMessage="You can download your requested documents below. Click the download button next to each file to save it to your device."
            description="sub header"
          />
        </Text>
        {docDetails}
      </Box>
    );
  }

  return (
    <IframeOrFullPageWrapper includeTosAndPrivacy withoutPadding>
      <Header
        showLanguage
        title={formatMessage({
          defaultMessage: "GDPR",
          description: "Header title",
        })}
        onCloseButtonClick={!hideExitButtons ? handleCancel : undefined}
      />
      {content}
    </IframeOrFullPageWrapper>
  );
};

export default GDPR;
